import * as React from 'react';

import {
  Button,
  DateRangePicker,
  SearchField,
  Toggle,
  ToggleButtonGroup,
} from '@rematter/ui-web';
import { useAtom, useAtomValue } from 'jotai';
import { LucideX } from 'lucide-react';

import * as state from '../../_state/bookingsList.state';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { useDebouncedAtom } from '~/shared/hooks/useDebouncedAtom';

export const BookingListFilters = () => {
  const t = useTranslations();

  // Atoms
  const [searchTerm, setSearchTerm] = useDebouncedAtom(state.$searchTerm, 300);
  const [selectedStatus, setSelectedStatus] = useAtom(state.$selectedStatus);
  const [dateRange, setDateRange] = useAtom(state.$dateRange);
  const { data: statusCounts } = useAtomValue(state.$bookingsCountingByStatus);

  // Memoized values
  const allCount = React.useMemo(
    () =>
      (statusCounts?.voided ?? 0) +
      (statusCounts?.draft ?? 0) +
      (statusCounts?.open ?? 0) +
      (statusCounts?.shipped ?? 0) +
      (statusCounts?.completed ?? 0),
    [statusCounts],
  );
  const hasActiveFilters = React.useMemo(
    () =>
      (searchTerm !== undefined && searchTerm?.trim() !== '') ||
      (selectedStatus !== undefined && selectedStatus !== 'all') ||
      !!dateRange,
    [searchTerm, selectedStatus, dateRange],
  );

  // Callbacks
  const handleClearAll = React.useCallback(() => {
    setSearchTerm('');
    setSelectedStatus(undefined);
    setDateRange(null);
  }, [setSearchTerm, setSelectedStatus, setDateRange]);

  return (
    <>
      <ToggleButtonGroup
        selectionMode="single"
        selectedKeys={selectedStatus ? [selectedStatus] : ['all']}
        onSelectionChange={(v) => {
          const value = Array.from(v.values())[0] as string;
          setSelectedStatus(value === 'all' ? undefined : value);
        }}
      >
        <Toggle id="all" badge={allCount}>
          {t.bookingsView.filters.status.all}
        </Toggle>
        <Toggle
          id="draft"
          badge={statusCounts?.draft ?? 0}
          isDisabled={!statusCounts?.draft}
        >
          {t.bookingsView.filters.status.draft}
        </Toggle>
        <Toggle
          id="open"
          badge={statusCounts?.open}
          isDisabled={!statusCounts?.open}
        >
          {t.bookingsView.filters.status.open}
        </Toggle>
        <Toggle
          id="shipped"
          badge={statusCounts?.shipped}
          isDisabled={!statusCounts?.shipped}
        >
          {t.bookingsView.filters.status.shipped}
        </Toggle>
        <Toggle
          id="closed"
          badge={(statusCounts?.completed ?? 0) + (statusCounts?.voided ?? 0)}
          isDisabled={!statusCounts?.completed && !statusCounts?.voided}
        >
          {t.bookingsView.filters.status.closed}
        </Toggle>
      </ToggleButtonGroup>
      <SearchField
        autoFocus
        placeholder={t.bookingsView.filters.search.placeholder}
        aria-label={t.bookingsView.filters.search.placeholder}
        value={searchTerm}
        className="w-[200px]"
        onChange={(value) => setSearchTerm(value ?? '')}
      />
      <DateRangePicker
        value={dateRange}
        onChange={setDateRange}
        label=""
        aria-label={t.bookingsView.filters.dateRange.placeholder}
        className="min-w-[200px]"
      />
      {hasActiveFilters && (
        <Button
          variant="text"
          color="primary"
          icon={<LucideX />}
          onPress={handleClearAll}
        >
          {t.bookingsView.filters.clearAll}
        </Button>
      )}
    </>
  );
};
